import { Label, LabelType, LabelValue } from '../../types';

import { parseSingleColumnCsvValues } from './LabelPanel.csv.utils';

const EMPTY_LABEL: Label = {
	key: '',
	displayName: '',
	description: '',
	type: LabelType.CUSTOM,
	values: []
};

export const cloneLabel = (label: Label | null): Label =>
	label
		? { ...label, values: label.values.map((v) => ({ ...v })) }
		: { ...EMPTY_LABEL, values: [] };

/**
 * Allowed characters for the label display name (trimmed input).
 * Matches the full string: Unicode letters and numbers, spaces, hyphen, underscore only.
 * Used by {@link validateDisplayName} against the trimmed value.
 */
const DISPLAY_NAME_PATTERN = /^[\p{L}\p{N}\s\-_]*$/u;

/**
 * Allowed characters for the optional description field.
 * Same as display name, plus comma and period.
 * Used by {@link validateDescription} when the description is non-empty.
 */
const DESCRIPTION_PATTERN = /^[\p{L}\p{N}\s\-_,.]*$/u;

/**
 * Allowed characters for a label value (trimmed input in the values list).
 * Same character set as {@link DISPLAY_NAME_PATTERN}.
 * Used by {@link validateNewValue} against the trimmed value.
 */
const VALUE_PATTERN = /^[\p{L}\p{N}\s\-_.]*$/u;

/**
 * Allowed characters for the label key (trimmed input).
 * Restricted to ASCII letters, hyphen, and underscore so the key stays a safe programmatic
 * identifier (configuration, workflows, integrations) without spaces or Unicode, which the display name allows.
 * Used by {@link validateKey} against the trimmed value.
 */
const KEY_PATTERN = /^[A-Za-z\-_]+$/;

const DISPLAY_NAME_MAX_LENGTH = 30;
const DESCRIPTION_MAX_LENGTH = 100;
const VALUE_MAX_LENGTH = 50;
const KEY_MAX_LENGTH = 30;

export const LABEL_PANEL_VALIDATION = {
	DISPLAY_NAME_MAX_LENGTH,
	DESCRIPTION_MAX_LENGTH,
	VALUE_MAX_LENGTH,
	KEY_MAX_LENGTH
} as const;

const VALIDATION_ERRORS = {
	DISPLAY_NAME_REQUIRED: 'Display name is required',
	DISPLAY_NAME_TOO_LONG: `Display name must be ${DISPLAY_NAME_MAX_LENGTH} characters or less`,
	DISPLAY_NAME_INVALID:
		'Display name can only contain letters, numbers, spaces, hyphens, and underscores',
	DESCRIPTION_TOO_LONG: `Description must be ${DESCRIPTION_MAX_LENGTH} characters or less`,
	DESCRIPTION_INVALID:
		'Description can only contain letters, numbers, spaces, hyphens, underscores, commas, and periods',
	VALUE_REQUIRED: 'Value is required',
	VALUE_TOO_LONG: `Value must be ${VALUE_MAX_LENGTH} characters or less`,
	VALUE_INVALID:
		'Value can only contain letters, numbers, spaces, hyphens, underscores, and periods',
	VALUE_DUPLICATE: 'This value already exists',
	KEY_REQUIRED: 'Key is required',
	KEY_TOO_LONG: `Key must be ${KEY_MAX_LENGTH} characters or less`,
	KEY_INVALID: 'Key can only contain letters, hyphens, and underscores',
	KEY_DUPLICATE: 'This key already exists'
} as const;

export const validateDisplayName = (value: string): string | undefined => {
	const trimmed = value.trim();

	if (!trimmed) {
		return VALIDATION_ERRORS.DISPLAY_NAME_REQUIRED;
	}

	if (trimmed.length > DISPLAY_NAME_MAX_LENGTH) {
		return VALIDATION_ERRORS.DISPLAY_NAME_TOO_LONG;
	}

	if (!DISPLAY_NAME_PATTERN.test(trimmed)) {
		return VALIDATION_ERRORS.DISPLAY_NAME_INVALID;
	}

	return undefined;
};

export const validateDescription = (value: string): string | undefined => {
	if (!value) {
		return undefined;
	}

	if (value.length > DESCRIPTION_MAX_LENGTH) {
		return VALIDATION_ERRORS.DESCRIPTION_TOO_LONG;
	}

	if (!DESCRIPTION_PATTERN.test(value)) {
		return VALIDATION_ERRORS.DESCRIPTION_INVALID;
	}

	return undefined;
};

/**
 * Validates format and length of a non-empty label value (no duplicate check).
 * Used by {@link validateNewValue}, {@link processLabelValuesCsvUpload}, and CSV error report download.
 */
export const validateLabelValueFormat = (trimmed: string): string | undefined => {
	if (trimmed.length > VALUE_MAX_LENGTH) {
		return VALIDATION_ERRORS.VALUE_TOO_LONG;
	}

	if (!VALUE_PATTERN.test(trimmed)) {
		return VALIDATION_ERRORS.VALUE_INVALID;
	}

	return undefined;
};

export const validateNewValue = (
	value: string,
	existingValues: LabelValue[]
): string | undefined => {
	const trimmed = value.trim();

	if (!trimmed) {
		return VALIDATION_ERRORS.VALUE_REQUIRED;
	}

	const formatError = validateLabelValueFormat(trimmed);
	if (formatError) {
		return formatError;
	}

	if (existingValues.some((v) => v.value.toLowerCase() === trimmed.toLowerCase())) {
		return VALIDATION_ERRORS.VALUE_DUPLICATE;
	}

	return undefined;
};

/**
 * Result of parsing a CSV for label values. If {@link emptyFile} is true or {@link invalid}
 * is non-empty, {@link nextValues} equals `existingValues` (no partial upload).
 */
type LabelValuesCsvUploadResult = {
	nextValues: LabelValue[];
	invalid: string[];
	emptyFile: boolean;
	/** All parsed values in file order — set when the upload is rejected due to invalid rows (for error report download). */
	rejectedFileValues?: string[];
	/** Set on successful parse when at least one row was added or skipped as duplicate. */
	addedCount?: number;
	skippedDuplicateCount?: number;
};

/**
 * Parses CSV content and merges valid values into `existingValues`.
 * Per product rules: if the file has no values, or any row fails format validation,
 * the entire upload is rejected — no rows are applied.
 * Duplicate values (already present or repeated in the file) are skipped when the file is accepted.
 */
const processLabelValuesCsvUpload = (
	text: string,
	existingValues: LabelValue[]
): LabelValuesCsvUploadResult => {
	const rawValues = parseSingleColumnCsvValues(text);

	if (rawValues.length === 0) {
		return {
			nextValues: existingValues,
			invalid: [],
			emptyFile: true
		};
	}

	const invalid: string[] = [];
	for (const trimmed of rawValues) {
		const formatError = validateLabelValueFormat(trimmed);
		if (formatError) {
			invalid.push(trimmed);
		}
	}

	if (invalid.length > 0) {
		return {
			nextValues: existingValues,
			invalid,
			emptyFile: false,
			rejectedFileValues: rawValues
		};
	}

	const seenLower = new Set(existingValues.map((v) => v.value.toLowerCase()));
	const toAdd: LabelValue[] = [];
	let skippedDuplicateCount = 0;

	for (const trimmed of rawValues) {
		const lower = trimmed.toLowerCase();
		if (seenLower.has(lower)) {
			skippedDuplicateCount += 1;
			continue;
		}
		seenLower.add(lower);
		toAdd.push({ value: trimmed, dashboardVisible: true, isSessionNew: true });
	}

	return {
		nextValues: [...existingValues, ...toAdd],
		invalid: [],
		emptyFile: false,
		addedCount: toAdd.length,
		skippedDuplicateCount
	};
};

export const computeCsvUploadUpdate = (
	text: string,
	prevLabel: Label
): { nextLabel: Label; uploadResult: LabelValuesCsvUploadResult } => {
	const uploadResult = processLabelValuesCsvUpload(text, prevLabel.values);
	const nextLabel =
		uploadResult.emptyFile || uploadResult.invalid.length > 0
			? prevLabel
			: { ...prevLabel, values: uploadResult.nextValues };
	return { nextLabel, uploadResult };
};

/** New session values first (A→Z), then the rest (A→Z). */
export const sortLabelValuesForDisplay = (values: LabelValue[]): LabelValue[] => {
	const withNew = values.filter((v) => v.isSessionNew);
	const without = values.filter((v) => !v.isSessionNew);
	const byValue = (a: LabelValue, b: LabelValue): number =>
		a.value.localeCompare(b.value, undefined, { sensitivity: 'base' });
	return [...withNew.sort(byValue), ...without.sort(byValue)];
};

export const validateKey = (value: string, existingKeys: string[]): string | undefined => {
	const trimmed = value.trim();

	if (!trimmed) {
		return VALIDATION_ERRORS.KEY_REQUIRED;
	}

	if (trimmed.length > KEY_MAX_LENGTH) {
		return VALIDATION_ERRORS.KEY_TOO_LONG;
	}

	if (!KEY_PATTERN.test(trimmed)) {
		return VALIDATION_ERRORS.KEY_INVALID;
	}

	if (existingKeys.some((k) => k.toLowerCase() === trimmed.toLowerCase())) {
		return VALIDATION_ERRORS.KEY_DUPLICATE;
	}

	return undefined;
};
